/**
 * DataDrivenEntityList - Fully Data-Driven List Component
 *
 * Renders entity lists based on SubgraphMetadata entity types configuration.
 * Automatically loads list schemas from backend and generates UI.
 *
 * FERP Principles:
 * - Data-driven: List schema from SubgraphMetadata.entityTypes[].listSchemaId
 * - NO hardcoded columns or filters
 * - Life-critical: Safety levels and compliance from config
 *
 * Usage:
 * ```tsx
 * // List all Patients - columns/filters from config
 * <DataDrivenEntityList
 *   entityName="Patient"
 *   onRowClick={(row) => navigate(`/patients/${row.id}`)}
 * />
 *
 * // List with create button
 * <DataDrivenEntityList
 *   entityName="Employee"
 *   onCreateClick={() => openCreateModal()}
 * />
 * ```
 */

import React, { useState, useMemo } from 'react';
import {
  Stack,
  Alert,
  Skeleton,
  Badge,
  Group,
  Text,
  Card,
  TextInput,
  Button,
  Table,
  ActionIcon,
  Menu,
  Pagination,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconShieldCheck,
  IconSearch,
  IconPlus,
  IconDotsVertical,
  IconEye,
  IconPencil,
  IconArchive,
  IconRefresh,
  IconDatabase,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth';
import { useDataDrivenForm, type DataDrivenFormConfig } from '@/hooks/useDataDrivenForm';
// useListSchema and useListData will be used when backend list data fetching is implemented
// import { useListSchema, useListData } from '@/components/UniversalEntityForm/useFormSchema';

// ============================================================================
// Types
// ============================================================================

export interface DataDrivenEntityListProps {
  /** Entity name (e.g., "Patient", "Employee") - looked up in config */
  entityName: string;
  /** Callback when a row is clicked */
  onRowClick?: (row: Record<string, unknown>) => void;
  /** Callback when create button is clicked */
  onCreateClick?: () => void;
  /** Callback when edit action is triggered */
  onEditClick?: (row: Record<string, unknown>) => void;
  /** Callback when archive action is triggered */
  onArchiveClick?: (row: Record<string, unknown>) => void;
  /** Show create button (default: true if canCreate) */
  showCreateButton?: boolean;
  /** Page size (default: 20) */
  pageSize?: number;
  /** Initial filter values */
  initialFilters?: Record<string, unknown>;
  /** Hide the header/title section */
  hideHeader?: boolean;
}

// ============================================================================
// Sub-components
// ============================================================================

interface ListHeaderProps {
  config: DataDrivenFormConfig;
  onCreateClick?: () => void;
  showCreateButton: boolean;
  canCreate: boolean;
  onRefresh?: () => void;
}

function ListHeader({
  config,
  onCreateClick,
  showCreateButton,
  canCreate,
  onRefresh,
}: ListHeaderProps) {
  return (
    <Group justify="space-between" mb="md">
      <div>
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="blue">
            <IconDatabase size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {config.pluralName}
            </Text>
            <Text size="sm" c="dimmed">
              {config.description}
            </Text>
          </div>
        </Group>
      </div>
      <Group gap="sm">
        {config.isLifeCritical && (
          <Badge color="red" variant="filled" leftSection={<IconShieldCheck size={12} />}>
            Life-Critical
          </Badge>
        )}
        {config.complianceFrameworks.map((framework) => (
          <Badge key={framework} size="sm" variant="outline" color="gray">
            {framework}
          </Badge>
        ))}
        {onRefresh && (
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={onRefresh}>
            Refresh
          </Button>
        )}
        {showCreateButton && canCreate && (
          <Button leftSection={<IconPlus size={16} />} onClick={onCreateClick}>
            New {config.entityType}
          </Button>
        )}
      </Group>
    </Group>
  );
}

// ============================================================================
// Placeholder List (while full schema loading is implemented)
// ============================================================================

interface PlaceholderListProps {
  config: DataDrivenFormConfig;
  onRowClick?: (row: Record<string, unknown>) => void;
  onEditClick?: (row: Record<string, unknown>) => void;
  onArchiveClick?: (row: Record<string, unknown>) => void;
}

/**
 * Placeholder list showing that data-driven list is configured
 * but actual data loading requires backend schema implementation.
 */
function PlaceholderList({
  config,
  onRowClick,
  onEditClick,
  onArchiveClick,
}: PlaceholderListProps) {
  // Mock data for demonstration
  const mockRows = [
    { id: '1', [config.displayField]: `Sample ${config.entityType} 1`, status: 'active' },
    { id: '2', [config.displayField]: `Sample ${config.entityType} 2`, status: 'active' },
    { id: '3', [config.displayField]: `Sample ${config.entityType} 3`, status: 'inactive' },
  ];

  return (
    <Card withBorder>
      <Alert color="blue" variant="light" mb="md" icon={<IconDatabase />}>
        Data-driven list configured for <strong>{config.entityType}</strong>.
        {config.listSchemaId ? (
          <Text size="sm">
            List schema: <code>{config.listSchemaId}</code>
          </Text>
        ) : (
          <Text size="sm">
            No listSchemaId defined. Define one in SubgraphMetadata for full functionality.
          </Text>
        )}
      </Alert>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID</Table.Th>
            <Table.Th>{config.displayField}</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {mockRows.map((row) => (
            <Table.Tr
              key={row.id}
              onClick={() => onRowClick?.(row)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              <Table.Td>
                <Badge size="xs" variant="outline">
                  {row.id}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {row[config.displayField]}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge
                  size="sm"
                  color={row.status === 'active' ? 'green' : 'gray'}
                  variant="light"
                >
                  {row.status}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconDotsVertical size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEye size={14} />}
                      onClick={() => onRowClick?.(row)}
                    >
                      View
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPencil size={14} />}
                      onClick={() => onEditClick?.(row)}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconArchive size={14} />}
                      color="red"
                      onClick={() => onArchiveClick?.(row)}
                    >
                      Archive
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Group justify="space-between" mt="md">
        <Text size="sm" c="dimmed">
          Showing 3 sample records (mock data)
        </Text>
        <Pagination total={1} value={1} disabled />
      </Group>
    </Card>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function DataDrivenEntityList({
  entityName,
  onRowClick,
  onCreateClick,
  onEditClick,
  onArchiveClick,
  showCreateButton = true,
  pageSize: _pageSize = 20,
  initialFilters: _initialFilters,
  hideHeader = false,
}: DataDrivenEntityListProps): React.ReactElement {
  const tenantId = useTenantId();
  const { getFormForEntity, canCreate, canRead, loading, error } =
    useDataDrivenForm(tenantId);

  const [searchQuery, setSearchQuery] = useState('');
  const [_page, _setPage] = useState(1);

  // Get list configuration for this entity
  const listConfig = useMemo(() => {
    return getFormForEntity(entityName);
  }, [getFormForEntity, entityName]);

  // Check permissions
  const hasReadPermission = canRead(entityName);
  const hasCreatePermission = canCreate(entityName);

  // Loading state
  if (loading) {
    return (
      <Stack gap="md">
        <Skeleton height={60} />
        <Skeleton height={40} />
        <Skeleton height={200} />
        <Skeleton height={40} />
      </Stack>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert color="red" title="Configuration Error" icon={<IconAlertTriangle />}>
        Failed to load list configuration: {error.message}
      </Alert>
    );
  }

  // Entity not found in config
  if (!listConfig) {
    return (
      <Alert color="yellow" title="Entity Not Found" icon={<IconAlertTriangle />}>
        Entity type &quot;{entityName}&quot; is not defined in your tenant&apos;s SubgraphMetadata
        configuration.
      </Alert>
    );
  }

  // Permission denied
  if (!hasReadPermission) {
    return (
      <Alert color="red" title="Permission Denied" icon={<IconAlertTriangle />}>
        You do not have permission to view {listConfig.pluralName}.
        <Text size="sm" mt="xs">
          Required permission: {listConfig.permissions.list}
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      {!hideHeader && (
        <ListHeader
          config={listConfig}
          onCreateClick={onCreateClick}
          showCreateButton={showCreateButton}
          canCreate={hasCreatePermission}
        />
      )}

      {/* Search bar */}
      <TextInput
        placeholder={`Search ${listConfig.pluralName.toLowerCase()}...`}
        leftSection={<IconSearch size={16} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
      />

      {/* Placeholder list (actual implementation would use useListSchema/useListData) */}
      <PlaceholderList
        config={listConfig}
        onRowClick={onRowClick}
        onEditClick={onEditClick}
        onArchiveClick={onArchiveClick}
      />
    </Stack>
  );
}

export default DataDrivenEntityList;
